/*
 *   This content is licensed according to the W3C Software License at
 *   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 *   File:   tabs-automatic.js
 *
 *   Desc:   Tablist widget that implements ARIA Authoring Practices
 */

"use strict";

class TabsAutomatic {
	constructor(groupNode) {
		this.tablistNode = groupNode;
		this.container = groupNode.closest(".tabs-container");
		this.switcherLabel = this.container.querySelector(
			".tab-switcher-label span"
		);
		this.switcherSelect = this.container.querySelector(
			".tab-switcher select"
		);

		this.tabs = [];

		this.firstTab = null;
		this.lastTab = null;

		this.tabs = Array.from(this.tablistNode.querySelectorAll("[role=tab]"));
		this.tabpanels = [];

		for (var i = 0; i < this.tabs.length; i += 1) {
			var tab = this.tabs[i];
			var tabpanel = document.getElementById(
				tab.getAttribute("aria-controls")
			);

			tab.tabIndex = -1;
			tab.setAttribute("aria-selected", "false");
			this.tabpanels.push(tabpanel);

			tab.addEventListener("keydown", this.onKeydown.bind(this));
			tab.addEventListener("click", this.onClick.bind(this));
			let description = tab.querySelector(".description");
			if (description) {
				description.addEventListener(
					"transitionend",
					this.handleDescriptionVisibility.bind(this)
				);
			}

			if (!this.firstTab) {
				this.firstTab = tab;
			}
			this.lastTab = tab;
		}

		this.setSelectedTab(this.firstTab, false);
		if (this.switcherSelect) {
			this.switcherSelect.addEventListener(
				"change",
				this.onChange.bind(this)
			);
		}
	}

	handleDescriptionVisibility(event) {
		if (event.target.offsetHeight < 1) {
			event.target.setAttribute("hidden", "");
		}
	}

	setSelectedTab(currentTab, setFocus) {
		if (typeof setFocus !== "boolean") {
			setFocus = true;
		}

		for (var i = 0; i < this.tabs.length; i += 1) {
			var tab = this.tabs[i];
			let description = tab.querySelector(".description");

			// prevent focus on hidden tab content elements
			let focusable = this.tabpanels[i].querySelectorAll("button, a");
			focusable.forEach((el) => {
				el.tabIndex = -1;
			});

			if (currentTab === tab) {
				tab.setAttribute("aria-selected", "true");
				tab.removeAttribute("tabindex");
				this.tabpanels[i].classList.remove("is-hidden");
				this.tabpanels[i].tabIndex = 0;
				if (setFocus) {
					tab.focus();
					if (description) {
						description.removeAttribute("hidden");
						// get how tall this should be
						description.style.height = "auto";
						let height = description.offsetHeight + "px";
						// immediately hide it
						description.style.height = "0px";
						// then add the height so css can animate it
						setTimeout(() => {
							description.style.height = height;
						}, 0);
					}
				} else {
					if (description) {
						// define the height on the first tab on init
						description.style.height =
							description.offsetHeight + "px";
					}
				}
				// re-enable focus on active tab content
				focusable.forEach((el) => {
					el.removeAttribute("tabindex");
				});
			} else {
				tab.setAttribute("aria-selected", "false");
				tab.tabIndex = -1;
				this.tabpanels[i].classList.add("is-hidden");
				this.tabpanels[i].tabIndex = -1;
				if (description) {
					description.style.height = "0px";
				}
			}
		}

		// if these tabs are in a dialog, we need to prevent break out back to page content
		// this doesn't run on init, but will each time the tab is changed afterwards
		let trap = currentTab.closest('[role="dialog"]');
		if (setFocus && trap) {
			LS.trapFocus(`#${trap.id}`, false);
		}
		// mobile UI
		if (this.switcherSelect) {
			this.switcherLabel.textContent = currentTab.textContent;
			this.switcherSelect.value = currentTab.dataset.handle;
		}
	}

	setSelectedToPreviousTab(currentTab) {
		var index;

		if (currentTab === this.firstTab) {
			this.setSelectedTab(this.lastTab);
		} else {
			index = this.tabs.indexOf(currentTab);
			this.setSelectedTab(this.tabs[index - 1]);
		}
	}

	setSelectedToNextTab(currentTab) {
		var index;

		if (currentTab === this.lastTab) {
			this.setSelectedTab(this.firstTab);
		} else {
			index = this.tabs.indexOf(currentTab);
			this.setSelectedTab(this.tabs[index + 1]);
		}
	}

	/* EVENT HANDLERS */

	onKeydown(event) {
		var tgt = event.currentTarget,
			flag = false;

		switch (event.key) {
			case "ArrowUp":
				this.setSelectedToPreviousTab(tgt);
				flag = true;
				break;
			case "ArrowLeft":
				this.setSelectedToPreviousTab(tgt);
				flag = true;
				break;

			case "ArrowDown":
				this.setSelectedToNextTab(tgt);
				flag = true;
				break;
			case "ArrowRight":
				this.setSelectedToNextTab(tgt);
				flag = true;
				break;

			case "Home":
				this.setSelectedTab(this.firstTab);
				flag = true;
				break;

			case "End":
				this.setSelectedTab(this.lastTab);
				flag = true;
				break;

			default:
				break;
		}

		if (flag) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	onClick(event) {
		this.setSelectedTab(event.currentTarget);
	}

	onChange(event) {
		this.tabs.forEach((tab) => {
			if (tab.dataset.handle === this.switcherSelect.value) {
				this.setSelectedTab(tab);
			}
		});
	}
}

// Initialize tablist

window.addEventListener("load", function () {
	var tablists = document.querySelectorAll("[role=tablist].automatic");
	for (var i = 0; i < tablists.length; i++) {
		new TabsAutomatic(tablists[i]);
	}
});
